import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { createRoot } from 'react-dom/client';

import { getProductData, getSearchResults } from '../../api/search';
import ModalComponent from '../../components/modals/ModalComponent';
import { SearchBar } from '../../components/search/SearchBar';
import { SearchResultCard } from '../../components/search/SearchResultCard';
import { getRequestedLanguages } from '../../locale/languages';
import { ProductsEdges, SearchResults } from '../../utils/models/search';

const CustomSearchPage: FC = () => {
    const language = getRequestedLanguages(window.berardi_accountUserData.language);
    const [query, setQuery] = useState<string | null>(null);
    const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
    const [productData, setProductData] = useState<ProductsEdges | null>(null);
    const [loading, setLoading] = useState(false);

    const productSearch = useCallback(async () => {
        setLoading(true);
        try {
            searchResults != null && setSearchResults(null);
            productData != null && setProductData(null);

            const accountCode = localStorage.getItem('br_currentUser')
                ? JSON.parse(localStorage.getItem('br_currentUser') as string).customerAccount
                : window.berardi_accountUserData.accountCode;

            if (query) {
                const searchResultsResponse = await getSearchResults(query, accountCode);
                if (searchResultsResponse) {
                    setSearchResults(searchResultsResponse.data);
                }
            }
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    }, [query]);

    const productDataQuery = useCallback(async () => {
        setLoading(true);
        try {
            if (searchResults && searchResults.data.length > 0) {
                const productIds = searchResults.data.map((data) => data.handle);
                const productDataResponse = await getProductData(productIds);
                const productArray = productDataResponse.data.data.products;
                setProductData(productArray);
            }
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    }, [searchResults]);

    const updateTopBarAndLink = useCallback(() => {
        if (query != null) {
            const customSearchHeaderForm: HTMLInputElement | null = document.querySelector('.custom-search-form input');
            if (customSearchHeaderForm) {
                customSearchHeaderForm.value = query;
            }
            const customerSearchHeaderMobileForm: HTMLInputElement | null = document.querySelector(
                'search-form.search-modal__form input.search__input',
            );
            if (customerSearchHeaderMobileForm) {
                customerSearchHeaderMobileForm.value = query;
            }
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.set('q', encodeURIComponent(query.trim()));
            window.history.pushState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
        }
    }, [query]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams) {
            const queryParameter: string | null = urlParams.get('q');
            if (queryParameter) {
                setQuery(queryParameter);
            }
        }
    }, []);

    useEffect(() => {
        productSearch();
        updateTopBarAndLink();
    }, [query]);

    useEffect(() => {
        productDataQuery();
    }, [searchResults]);

    return (
        <>
            <div className="br-search-bar-header">
                <div className="br-horizontal-tabs">
                    <a href="#" className="br-code-search-link selected">
                        <h3 className="h2">{language.search.cod_art}</h3>
                    </a>
                    <a href="/search" className="br-name-search-link">
                        <h3 className="h2">{language.search.name_art}</h3>
                    </a>
                </div>

                <SearchBar query={query} onSearchSubmit={setQuery}></SearchBar>
            </div>

            <ModalComponent isLoading showModal={loading} />

            <div className="product-grid-container page-width br-search-results">
                {productData && productData.edges.length > 0 && !loading && (
                    <ul
                        className="grid product-grid  grid--2-col-tablet-down grid--4-col-desktop"
                        id="ProductGridContainer"
                    >
                        {productData.edges.map((product) => (
                            <SearchResultCard product={product} />
                        ))}
                    </ul>
                )}
                {!loading && (!productData || productData?.edges.length == 0) && (
                    <h3 className="noProductsFound">
                        {query ? language.search.no_product_found : language.search.no_query}
                    </h3>
                )}
            </div>
        </>
    );
};

const CustomSearchPageId = document.getElementById('CustomSearchPage');
console.log('🚀 ~ CustomSearchPage:', CustomSearchPageId);
const root = CustomSearchPageId && createRoot(CustomSearchPageId as HTMLElement);
root && root.render(<CustomSearchPage />);
